import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { RATE_DIRECTION, type RateDirection } from './types';

export interface ManualFxQuote {
  sell_amount: string;
  sell_currency_code: string;
  buy_amount: string;
  buy_currency_code: string;
  lp_rate: string;
  client_rate: string;
  lp_cost: string;
  margin: string;
  margin_currency_code: string;
}

export interface ManualFxQuoteInput {
  sellAmount: string;
  sellCurrencyCode: string;
  buyCurrencyCode: string;
  rateDirection: RateDirection;
  lpRate: string;
  clientRate: string;
  marginCurrencyCode: string;
}

/**
 * A rate typed the other way round means its reciprocal.
 *
 * The server only ever speaks in "1 sold = rate bought", so the inversion
 * happens once, here, rather than being threaded through the arithmetic.
 */
export function toSellToBuyRate(rate: string, direction: RateDirection): string | null {
  if (!/^\d{1,12}(\.\d{1,8})?$/.test(rate.trim())) return null;
  if (direction === RATE_DIRECTION.SELL_TO_BUY) return rate.trim();

  const value = Number(rate);
  if (!Number.isFinite(value) || value === 0) return null;
  // Eight decimals is what `numeric(20,8)` holds; the server re-reads the
  // figure at that scale either way.
  return (1 / value).toFixed(8);
}

function isReady(input: ManualFxQuoteInput): boolean {
  return (
    toSellToBuyRate(input.lpRate, input.rateDirection) !== null &&
    toSellToBuyRate(input.clientRate, input.rateDirection) !== null &&
    /^\d{1,16}(\.\d{1,4})?$/.test(input.sellAmount.trim()) &&
    Number(input.sellAmount) > 0 &&
    input.sellCurrencyCode !== input.buyCurrencyCode
  );
}

/**
 * The recap the operator reads before committing (#273).
 *
 * Asked of the server rather than worked out here: these are the figures
 * that will be booked, and a screen that rounds differently from the ledger
 * is worse than one that shows nothing. Debounced so a half-typed rate does
 * not fire a request per keystroke.
 */
export function useManualFxQuote(input: ManualFxQuoteInput): {
  quote: ManualFxQuote | null;
  isQuoting: boolean;
  error: string | null;
} {
  const [settled, setSettled] = useState(input);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(input), 350);
    return () => clearTimeout(timer);
    // Les champs, pas l'identite de l'objet : l'appelant en reconstruit un
    // a chaque rendu, et dependre de lui relancerait le minuteur sans fin.
  }, [
    input.sellAmount,
    input.sellCurrencyCode,
    input.buyCurrencyCode,
    input.rateDirection,
    input.lpRate,
    input.clientRate,
    input.marginCurrencyCode,
  ]);

  const ready = isReady(settled);

  const query = useQuery<ManualFxQuote>({
    queryKey: ['manual-fx-quote', settled],
    enabled: ready,
    retry: false,
    queryFn: async () =>
      api.post<ManualFxQuote>('/api/convera/fx/manual/quote', {
        sell_amount: settled.sellAmount,
        sell_currency_code: settled.sellCurrencyCode,
        buy_currency_code: settled.buyCurrencyCode,
        lp_rate: toSellToBuyRate(settled.lpRate, settled.rateDirection),
        client_rate: toSellToBuyRate(settled.clientRate, settled.rateDirection),
        margin_currency_code: settled.marginCurrencyCode,
      }),
  });

  // Le devis repond a `settled`, qui traine de 350 ms derriere la saisie.
  // Dans cet intervalle il decrit encore les anciens chiffres : rendre ce
  // devis-la laissait l'operateur confirmer un montant qu'il n'avait pas lu.
  const isCurrent =
    settled.sellAmount === input.sellAmount &&
    settled.sellCurrencyCode === input.sellCurrencyCode &&
    settled.buyCurrencyCode === input.buyCurrencyCode &&
    settled.rateDirection === input.rateDirection &&
    settled.lpRate === input.lpRate &&
    settled.clientRate === input.clientRate &&
    settled.marginCurrencyCode === input.marginCurrencyCode;

  return {
    quote: ready && isCurrent && query.data ? query.data : null,
    isQuoting: (ready && query.isFetching) || !isCurrent,
    error: query.error && isCurrent ? 'Could not work out this conversion' : null,
  };
}
